import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MetricsApiLogPathDatum } from 'dashboard/models/metrics-api-logs';
import { getAdjustedFilters } from 'dashboard/redux/selectors/metrics-api-logs';
import {
  clearMetrics,
  initialState,
  MetricsApiLogFilterParams,
  resetMetricsDateFilter,
  setMetricsApiLogFilters,
} from 'dashboard/redux/slices/metrics-api-logs';
import { RootState } from 'dashboard/redux/store';
import {
  loadMetricsApiLogsPaths,
  loadMetricsApiLogsVolumeOverTime,
} from 'dashboard/redux/thunk-actions/metrics-api-logs';
import DashboardApiAnalyticsView from 'dashboard/views/DashboardApiAnalyticsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useEffectOnEnvironmentChange from 'lib/hooks/useOnEnvironmentChange';
import useTable from 'lib/hooks/useTable';
import { Iso8601DateString } from 'lib/types';
import { translateDateValuetoLuxon } from 'lib/utils/filters';

const findSelectedApiPaths = (
  filters: MetricsApiLogFilterParams,
  apiPaths: MetricsApiLogPathDatum[],
): MetricsApiLogPathDatum[] => {
  return apiPaths.filter((path) => {
    const match = filters.apiPaths.find((p) => {
      return p.request_path === path.request_path && p.request_method === path.request_method;
    });
    return !!match;
  });
};

const DashboardApiAnalyticsRoute = (props: PropsFromRedux) => {
  const { currentEnvironment, filters, apiPaths, volumeOverTime } = props;

  useEffectOnEnvironmentChange(() => {
    props.clearMetrics();
  });

  const handleApiPathFilterUpdate = useCallback((apiPaths: string[]) => {
    props.setMetricsApiLogFilters({
      apiPaths: apiPaths.map((p) => {
        const pathComponents = p.split(' ');
        return {
          request_method: pathComponents[0],
          request_path: pathComponents[1],
          count: parseInt(pathComponents[2]),
        };
      }),
    });
  }, []);

  const { callback: loadApiLogMetrics } = useApiCallback(async () => {
    const overrideFilters = {
      ...props.filters,
      apiPaths: selectedApiPaths || [],
    };
    await props.loadMetricsApiLogsVolumeOverTime(currentEnvironment, overrideFilters);
  }, [currentEnvironment, filters]);

  const { callback: loadApiLogsPaths } = useApiCallback(async () => {
    const overrideFilters = {
      ...filters,
      createdAtStart: DateTime.fromISO(filters.createdAtEnd as Iso8601DateString)
        .minus({ months: 6 })
        .toISO() as Iso8601DateString,
    };
    await props.loadMetricsApiLogsPaths(currentEnvironment, overrideFilters);
  }, [currentEnvironment]);

  const { handleDateUpdate } = useTable({
    filters,
    setFilters: props.setMetricsApiLogFilters,
  });

  const selectedApiPaths = useMemo(
    () => findSelectedApiPaths(filters, apiPaths.data),
    [filters, apiPaths],
  );

  useEffect(() => {
    if (props.volumeOverTime === initialState.apiLogsVolumeOverTime) {
      loadApiLogsPaths();
    }
  }, []);

  const handleDateUpdateOrClearV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      if (newDateRange) {
        const { startDate, endDate } = translateDateValuetoLuxon(newDateRange);
        startDate || endDate
          ? handleDateUpdate({ startDate, endDate })
          : props.resetMetricsDateFilter();
      }
    },
    [handleDateUpdate, props.resetMetricsDateFilter],
  );

  return (
    <DashboardApiAnalyticsView
      queriedAt={null}
      apiLogFilters={filters}
      apiPaths={apiPaths}
      selectedApiPaths={selectedApiPaths}
      volumeOverTime={volumeOverTime}
      loadApiLogMetrics={loadApiLogMetrics}
      handleApiPathFilterUpdate={handleApiPathFilterUpdate}
      handleDateUpdateV2={handleDateUpdateOrClearV2}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  filters: getAdjustedFilters(state),
  apiPaths: state.metricsApiLogs.apiLogsPaths,
  volumeOverTime: state.metricsApiLogs.apiLogsVolumeOverTime,
});

const mapDispatchToProps = {
  setMetricsApiLogFilters,
  clearMetrics,
  loadMetricsApiLogsVolumeOverTime,
  loadMetricsApiLogsPaths,
  resetMetricsDateFilter,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardApiAnalyticsRoute);
